import React from 'react';
import capitalize from 'lodash/capitalize';
import { Text } from '@atlaskit/primitives';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { DynamicResultTable, FormattedDate } from '@css-xp/base';
import { CloudProductDetailView, EntitlementStatus, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import {
  BillingFrequency,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  BillingStatus,
  FetchSandboxParentQuery,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  isFutureDatedInactiveSubscription,
  getChargeElementV2,
  ChargeElementType,
  isOrgProduct,
} from '@css-xp/ppc-utils';
import {
  errorStateProp,
  emptyStateProp,
  CloudSiteLink,
  NoData,
  TableHeading,
  genericEmptyStateProp,
  Usage,
} from '@css-xp/ppc-common';
import { getLatestPeriodEndDate } from '@css-xp/entitlements-v2';
import { AppsCountCell } from '@css-xp/ppc-subscription-table';
import { useFetchLinkedEntitlementsPPCQuery } from '../../../hooks/use-fetch-linked-entitlements-ppc-query';

export interface LinkedEntitlementsProps {
  id: string | undefined;
  transactionAccountId?: string | null;
  currentEntitlementType?: LinkedEntitlementTypes;
  productType: LinkedProductTypes;
  entitlementId: string;
  baseProduct?: string;
  isFutureDatedEntitlementEnabled?: boolean;
  isDesignIteration1Enabled?: boolean;
  isAddon?: boolean;
  isApp?: boolean;
  isEnterpriseInstanceEntitlement?: boolean;
  parentProductEntitlements?: LinkedDestinationEntitlement[];
  isSandbox?: boolean;
  sandboxParentDetails?: {
    parent: FetchSandboxParentQuery | undefined;
    loading: boolean;
  };
}

export const EnabledOnTab = ({
  id,
  transactionAccountId,
  currentEntitlementType,
  productType,
  entitlementId,
  baseProduct,
  isFutureDatedEntitlementEnabled = false,
  isDesignIteration1Enabled,
  isAddon,
  isApp,
  isEnterpriseInstanceEntitlement,
  parentProductEntitlements,
  isSandbox,
  sandboxParentDetails,
}: LinkedEntitlementsProps) => {
  let destinationEntitlementType = [LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT];
  if (isAddon) {
    destinationEntitlementType = [LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT];
  } else if (isEnterpriseInstanceEntitlement) {
    destinationEntitlementType = [LinkedEntitlementTypes.ENTERPRISE_PARENT];
  }

  const { data, loading, error } = useFetchLinkedEntitlementsPPCQuery({
    entitlementId: entitlementId,
    containerId: id,
    currentEntitlementType: currentEntitlementType,
    destinationEntitlementType,
    productType: productType,
    findProducts: true,
    findApps: false,
    transactionAccountId: transactionAccountId,
    skipOption: isApp || isSandbox, // we get the parent data from props hence no need to fetch
  });

  let linkedParentEntitlements = data?.entitlementDetails?.linkedEntitlements as Array<LinkedDestinationEntitlement>;

  if (isApp && isDesignIteration1Enabled) {
    const highestUsageParentEntitlement = parentProductEntitlements?.sort(
      (a, b) => Number(b?.activeUsers ?? 0) - Number(a?.activeUsers ?? 0)
    )?.[0];

    linkedParentEntitlements = highestUsageParentEntitlement
      ? ([highestUsageParentEntitlement] as Array<LinkedDestinationEntitlement>)
      : [];
  }

  if (isSandbox && sandboxParentDetails?.parent && isDesignIteration1Enabled) {
    linkedParentEntitlements = [sandboxParentDetails?.parent?.entitlementDetails as LinkedDestinationEntitlement];
  }

  const totalCount = linkedParentEntitlements?.length || 0;

  const getUsage = (entitlement: LinkedDestinationEntitlement) => {
    const billingFrequency = entitlement?.billingFrequency;
    const activeUsers = entitlement?.activeUsers;
    // TODO: find the range for annual billing cycle
    if (billingFrequency === BillingFrequency.ANNUAL) {
      const isStatusPage = isStatuspage(entitlement.productId);

      if (isStatusPage) return <NoData />;

      if (isDesignIteration1Enabled) {
        return (
          <Usage
            activeUsers={activeUsers}
            userTier={entitlement?.userTier}
            productId={entitlement?.productId}
            edition={entitlement?.edition as string}
            chargeElement={entitlement?.chargeElement}
          />
        );
      }

      return (
        <div>
          <div>{entitlement?.userTier}</div>
          <div className="n-300">User tier</div>
        </div>
      );
    } else {
      if (!activeUsers) return <NoData />;
      const usage = capitalize(getBilledForColumnData(entitlement?.productId, entitlement?.edition));
      const usageText = isDesignIteration1Enabled ? (
        <Text color="color.text.subtlest" size="small">
          {capitalize(
            getChargeElementV2({
              productId: entitlement?.productId as string,
              labelPlural: true,
              edition: entitlement?.edition as string,
              chargeElement: entitlement?.chargeElement ?? ChargeElementType.USER,
            })
          )}
        </Text>
      ) : (
        <div className="n-300">{usage}</div>
      );

      return (
        <div className="n-300">
          <div>{activeUsers}</div>
          {usageText}
        </div>
      );
    }
  };

  const getRows = () => {
    return linkedParentEntitlements
      ?.filter((entitlement) => {
        if (isDesignIteration1Enabled && isSandbox && !entitlement.productId) {
          return false;
        }

        return true;
      })
      ?.filter((entitlement) => entitlement.entitlementId !== entitlementId)
      ?.filter((entitlement) => (baseProduct ? entitlement.baseProduct === baseProduct : true))
      ?.map((item, index) => {
        const cloudSiteHostname = item?.cloudSiteHostname;
        const productDetailView = (
          <CloudProductDetailView
            logoId={item?.logoId as string}
            productId={item?.productId as string}
            entitlementNumber={item.entitlementNumber ?? ''}
            provisioningStatus={item?.provisioningStatus}
            productName={item?.productName}
            cloudSiteId={item?.cloudSiteId}
            trial={item?.trial ?? false}
            isSandbox={item?.isSandbox as boolean}
            productLink={item?.entitlementId ?? item?.entitlementNumber}
            version={isDesignIteration1Enabled ? 2 : undefined}
          />
        );
        const edition = item?.edition;
        const billingFrequency = item?.billingFrequency;

        const parentProductPlan =
          edition || billingFrequency ? (
            <div>
              <div>{edition ? capitalize(edition) : <NoData />}</div>
              {isDesignIteration1Enabled ? (
                <Text size="small" color="color.text.subtlest">
                  {billingFrequency ? capitalize(billingFrequency) : <NoData />}
                </Text>
              ) : (
                <div className="n-300">{billingFrequency ? capitalize(billingFrequency) : <NoData />}</div>
              )}
            </div>
          ) : (
            <NoData />
          );

        const isFutureDatedInactiveEntitlement =
          isFutureDatedEntitlementEnabled &&
          isFutureDatedInactiveSubscription(!!item?.isFutureDatedSubscription, item?.billingStatus as BillingStatus);

        const entitlementStatus = item?.billingStatus ? (
          <EntitlementStatus
            billingStatus={item.billingStatus}
            provisioningStatus={item?.provisioningStatus}
            isFutureDatedInactiveEntitlement={isFutureDatedInactiveEntitlement}
            hideInactiveStatus={isFutureDatedInactiveEntitlement}
          />
        ) : (
          <NoData />
        );

        const cloudSiteLink = (
          <CloudSiteLink
            siteName={cloudSiteHostname}
            txaId={transactionAccountId}
            cloudSiteId={item?.cloudSiteId}
            isOrgProductEntitlement={
              isDesignIteration1Enabled &&
              isOrgProduct({
                productIdOrKey: item?.productId,
                offeringName: item?.edition,
                relatesFromEntitlements: item?.relatesToEntitlements,
                offering: item?.offering,
              })
            }
          />
        );

        const quantityUsage = getUsage(item);

        const createdDate = item?.createdDate ? <FormattedDate date={item?.createdDate} /> : <NoData />;

        const latestPeriodEndDate = item?.latestPeriodEndDate ? (
          getLatestPeriodEndDate(item?.billingStatus, item?.latestPeriodEndDate)
        ) : (
          <NoData />
        );

        const appsCount = (
          <AppsCountCell
            version={isDesignIteration1Enabled ? 2 : undefined}
            appsCount={item?.linkedAppsId?.length ?? 0}
            subscriptionId={item.entitlementId}
          />
        );

        const cellsContent = (() => {
          if (isDesignIteration1Enabled) {
            return [
              {
                content: productDetailView,
              },
              {
                content: entitlementStatus,
              },
              {
                content: parentProductPlan,
              },
              {
                content: appsCount,
              },
              {
                content: cloudSiteLink,
              },
              {
                content: quantityUsage,
              },
              {
                content: latestPeriodEndDate,
              },
            ];
          }

          return [
            {
              content: productDetailView,
            },
            {
              content: parentProductPlan,
            },
            {
              content: entitlementStatus,
            },
            {
              content: cloudSiteLink,
            },
            {
              content: quantityUsage,
            },
            {
              content: createdDate,
            },
            {
              content: latestPeriodEndDate,
            },
          ];
        })();

        return {
          key: index.toString(),
          cells: cellsContent,
        };
      });
  };

  const LINKED_ENTITLEMENT_HEADER = {
    NAME: {
      content: isDesignIteration1Enabled ? 'Subscription' : 'Name',
      shouldTruncate: true,
      width: isDesignIteration1Enabled ? 8 : 20,
    },
    PLAN: {
      content: 'Plan',
      width: isDesignIteration1Enabled ? 5 : 10,
    },
    STATUS: {
      content: 'Status',
      width: isDesignIteration1Enabled ? 5 : 10,
    },
    SITE_NAME: {
      content: 'Site name',
      shouldTruncate: true,
      width: isDesignIteration1Enabled ? 8 : 15,
    },
    QUANTITY: {
      content: 'Quantity',
      width: isDesignIteration1Enabled ? 8 : 10,
    },
    CREATED_DATE: {
      content: 'Date added',
      width: 15,
    },
    NEXT_BILL_DATE: {
      content: isDesignIteration1Enabled ? 'Renew date' : 'Next bill date',
      width: 10,
    },
    APPS: {
      content: 'Apps',
      width: 4,
    },
  };

  const getHead = () => {
    const headerContent = (() => {
      if (isDesignIteration1Enabled) {
        return [
          LINKED_ENTITLEMENT_HEADER.NAME,
          LINKED_ENTITLEMENT_HEADER.STATUS,
          LINKED_ENTITLEMENT_HEADER.PLAN,
          LINKED_ENTITLEMENT_HEADER.APPS,
          LINKED_ENTITLEMENT_HEADER.SITE_NAME,
          LINKED_ENTITLEMENT_HEADER.QUANTITY,
          LINKED_ENTITLEMENT_HEADER.NEXT_BILL_DATE,
        ];
      }
      return [
        LINKED_ENTITLEMENT_HEADER.NAME,
        LINKED_ENTITLEMENT_HEADER.PLAN,
        LINKED_ENTITLEMENT_HEADER.STATUS,
        LINKED_ENTITLEMENT_HEADER.SITE_NAME,
        LINKED_ENTITLEMENT_HEADER.QUANTITY,
        LINKED_ENTITLEMENT_HEADER.CREATED_DATE,
        LINKED_ENTITLEMENT_HEADER.NEXT_BILL_DATE,
      ];
    })();
    return {
      cells: headerContent,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="linked-entitlements"
        beforeTable={isDesignIteration1Enabled ? undefined : <TableHeading>Parent subscriptions</TableHeading>}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
        isLoading={loading || (isDesignIteration1Enabled && sandboxParentDetails?.loading)}
        errorStateProps={errorStateProp}
        error={error?.message}
        totalCount={totalCount}
        hideHeadOnEmptyState={isDesignIteration1Enabled}
      />
    </div>
  );
};
